// src/components/dashboards/composition/compositionCatalogCache.ts
//
// The shared store behind `useCompositionCatalog` (plan 2026-10-13 dashboard
// calculated metrics, "Catalog freshness").
//
// MANY READERS, ONE CATALOG. The block editor, the edit panel, the "Add data
// source" modal, the report configurator and the Insights page each mount
// their own `useCompositionCatalog`. Once a builder can create a metric from a
// modal, every one of them must show it without a page reload. So the catalog
// lives here, in a module-level map with a listener set (the
// `useDashboardDataSources.ts` pattern; no react-query in this app), and a
// write anywhere calls `invalidateCompositionCatalog`.
//
// KEY. The catalog differs with and without a dashboard (with one it also
// carries the sources that dashboard shares), so the key is the dashboard id,
// and "" for the caller's own catalog.
//
// WHY A SEPARATE MODULE. It imports no API module, on purpose:
// - `useDashboardDataSources.ts` invalidates from here without importing the
//   hook (no cycle between the two modules), and
// - the global vitest setup resets it after every test. A setup file that
//   imported the hook would load `@/api/composition` before a test file's
//   `vi.mock` of it is registered, and the hook would keep the real function.

import type { CompositionCatalog } from "@/api/composition";

export type CatalogEvent =
  | { kind: "loaded"; catalog: CompositionCatalog }
  | { kind: "invalidated" };

type Listener = (key: string, event: CatalogEvent) => void;

const cache = new Map<string, CompositionCatalog>();
const inflight = new Map<string, Promise<CompositionCatalog>>();
const listeners = new Set<Listener>();

/** The cache key of a catalog: the dashboard id, or "" without one. */
export function catalogKey(dashboardId?: string | null): string {
  return dashboardId ?? "";
}

export function cachedCatalog(key: string): CompositionCatalog | undefined {
  return cache.get(key);
}

/** Follow every load and invalidation, of every key. Returns the unsubscribe. */
export function subscribeCatalog(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(key: string, event: CatalogEvent): void {
  for (const listener of Array.from(listeners)) listener(key, event);
}

/**
 * Run `request` for `key` unless one is already in flight, in which case join
 * that one: two readers mounting together cost one request. A success is
 * cached and published to every reader of the key; a failure is not cached.
 *
 * A request that an invalidation overtook (it was in flight when its key was
 * dropped) neither caches nor publishes what it got: that answer may predate
 * the write. Its awaiters are handed the request that replaced it when there
 * is one.
 */
export function sharedCatalogRequest(
  key: string,
  request: () => Promise<CompositionCatalog>,
): Promise<CompositionCatalog> {
  const pending = inflight.get(key);
  if (pending) return pending;
  const promise: Promise<CompositionCatalog> = request().then(
    (catalog) => {
      if (inflight.get(key) !== promise) return inflight.get(key) ?? catalog;
      inflight.delete(key);
      cache.set(key, catalog);
      emit(key, { kind: "loaded", catalog });
      return catalog;
    },
    (err: unknown) => {
      const current = inflight.get(key);
      if (current && current !== promise) return current;
      inflight.delete(key);
      throw err;
    },
  );
  inflight.set(key, promise);
  return promise;
}

/**
 * Drop cached catalogs; every mounted, enabled reader of a dropped key
 * refetches (one request per key, however many readers) and receives the new
 * catalog. Readers mounted later fetch on mount.
 *
 * With a dashboard id, only that dashboard's catalog is dropped: what an
 * attach, detach or table change of a dashboard data source calls, since only
 * that dashboard's catalog lists its shared sources.
 *
 * With no argument, every key is dropped. **A metric write calls it this
 * way**: a dashboard-level metric shows in that dashboard's catalog, an
 * account-level one (a promote) in every catalog, including the one without a
 * dashboard. Dropping all keys is one refetch per mounted key, and it cannot
 * miss a key the write touched.
 */
export function invalidateCompositionCatalog(dashboardId?: string | null): void {
  const keys =
    dashboardId === undefined
      ? Array.from(new Set([...cache.keys(), ...inflight.keys()]))
      : [catalogKey(dashboardId)];
  for (const key of keys) {
    cache.delete(key);
    inflight.delete(key);
  }
  if (dashboardId === undefined) {
    // A reader whose key was neither cached nor in flight (its last fetch
    // failed) refetches too: listeners filter on their own key.
    emit("*", { kind: "invalidated" });
  } else {
    for (const key of keys) emit(key, { kind: "invalidated" });
  }
}

/** Test seam: forget every cached catalog and in-flight request. Listeners
 *  belong to mounted hooks and leave with them. */
export function resetCompositionCatalogCache(): void {
  cache.clear();
  inflight.clear();
}
